import { useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play, Check } from "lucide-react";
import { toast } from "sonner";
import { Label } from "./ui/label";
import { voiceAPI } from "../lib/api";
import { PRESET_VOICES, describeVoice } from "../lib/voices";
import { useAuthStore } from "../lib/store";

const MODES = [
  { value: "auto", label: "Character speaks", hint: "Its own natural voice" },
  { value: "voiceover", label: "Add a voiceover", hint: "Pick a studio voice" },
  { value: "none", label: "No voice", hint: "Silent video" },
];

const VoiceRow = ({ voice, selected, playing, loading, onSelect, onPreview }) => (
  <div
    className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${
      selected ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50 bg-secondary/30 hover:border-border"
    }`}
  >
    <button type="button" onClick={onSelect} className="flex-1 text-left min-w-0" data-testid={`simple-voice-option-${voice.id}`}>
      <p className="text-sm text-white flex items-center gap-1">
        {selected && <Check className="w-3 h-3 text-[#00F0FF]" />}
        {voice.name}
      </p>
      <p className="text-xs text-muted-foreground truncate">{voice.tone}</p>
    </button>
    <button
      type="button"
      onClick={onPreview}
      className="w-8 h-8 shrink-0 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
      title="Play a sample"
      data-testid={`simple-voice-preview-${voice.id}`}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
    </button>
  </div>
);

export const SimpleVoicePicker = ({ mode, onModeChange, voiceId, onVoiceChange, clonedVoices = [] }) => {
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => () => audioRef.current?.pause(), []);

  const preview = async (id) => {
    audioRef.current?.pause();
    if (playingId === id) return setPlayingId(null);
    setLoadingId(id);
    try {
      const token = useAuthStore.getState().token;
      const res = await fetch(voiceAPI.getPreviewUrl(id), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(String(res.status));
      const url = URL.createObjectURL(await res.blob());
      const audio = new Audio(url);
      audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      audioRef.current = audio;
      await audio.play();
      setPlayingId(id);
    } catch {
      toast.error("Couldn't play this voice sample");
    } finally {
      setLoadingId(null);
    }
  };

  const groups = [
    ...(clonedVoices.length ? [{ title: "My voices", voices: clonedVoices.map((v) => ({ id: `cloned:${v.voice_id}`, name: v.name, tone: v.description || "Your cloned voice" })) }] : []),
    { title: "Female voices", voices: PRESET_VOICES.filter((v) => v.gender === "female") },
    { title: "Male voices", voices: PRESET_VOICES.filter((v) => v.gender === "male") },
  ];

  return (
    <div className="space-y-3" data-testid="simple-voice-picker">
      <Label className="text-white text-sm">Voice</Label>
      <div className="grid grid-cols-3 gap-2">
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => onModeChange(m.value)}
            className={`p-3 rounded-lg border-2 transition-all text-center ${
              mode === m.value ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-border/50 bg-secondary/30 hover:border-border"
            }`}
            data-testid={`simple-voice-${m.value}`}
          >
            <p className="text-sm text-white font-medium">{m.label}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{m.hint}</p>
          </button>
        ))}
      </div>
      {mode === "voiceover" && (
        <div className="space-y-3 p-3 rounded-lg border border-border/40 bg-black/20" data-testid="simple-voice-list">
          <p className="text-xs text-[#00F0FF]">The character's lips will be synced to the voice you pick. Tap play to hear a sample.</p>
          {groups.map((g) => (
            <div key={g.title} className="space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{g.title}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {g.voices.map((v) => (
                  <VoiceRow
                    key={v.id}
                    voice={v}
                    selected={voiceId === v.id}
                    playing={playingId === v.id}
                    loading={loadingId === v.id}
                    onSelect={() => onVoiceChange(v.id)}
                    onPreview={() => preview(v.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground" data-testid="simple-voice-summary">
        You'll get: {mode === "voiceover" ? (voiceId ? describeVoice(voiceId, clonedVoices) : "pick a voice above") : describeVoice(mode === "none" ? "visuals-only" : "none")}
      </p>
    </div>
  );
};
